import React, { useState, useEffect, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import 'chart.js/auto';
import { ChartOptions } from 'chart.js';
import { getGasConsumption } from 'services/gasConsumptionService';
import { getWeatherForecast } from 'services/weatherService';
import { toLocalDateString, toLocalDate, fromLocalDate } from 'utils/localTime';
import { GasConsumptionData, WeatherData } from 'types';

const dayBack = (time: Date) => {
  const [year, monthIndex, day, hours, minutes, seconds] = toLocalDate(time);
  const beginning = fromLocalDate(year, monthIndex, day, 0, 0, 0);
  const [prevYear, prevMonthIndex, prevDay] =
    toLocalDate(new Date(beginning.getTime() - 60 * 1000));
  return fromLocalDate(
    prevYear, prevMonthIndex, prevDay, hours, minutes, seconds
  );
};

const dayForward = (time: Date) => {
  const [year, monthIndex, day, hours, minutes, seconds] = toLocalDate(time);
  const end = fromLocalDate(year, monthIndex, day, 23, 59, 59);
  const [nextYear, nextMonthIndex, nextDay] =
    toLocalDate(new Date(end.getTime() + 60 * 1000));
  return fromLocalDate(
    nextYear, nextMonthIndex, nextDay, hours, minutes, seconds
  );
};

const HourlyTrendsPage: React.FC<{ startTime: Date }> = ({ startTime }) => {
  const [year, monthIndex, day] = toLocalDate(startTime);
  startTime = fromLocalDate(year, monthIndex, day, 0, 0, 0);

  const [currentTime, setCurrentTime] = useState(startTime);
  const [gasData, setGasData] = useState({
    projected: [] as GasConsumptionData[],
    actual: [] as GasConsumptionData[],
  });
  const [weatherData, setWeatherData] = useState<WeatherData[]>([]);

  const fetchGasData = useCallback(async () => {
    const endTime = dayForward(currentTime);
    const apiData = await getGasConsumption(currentTime, endTime, 'h');

    setGasData({
      projected: apiData['gas_consumption_projection'],
      actual: apiData['gas_consumption'],
    });
  }, [currentTime]);

  const fetchWeatherData = useCallback(async () => {
    const endTime = dayForward(currentTime);
    const weatherData = await getWeatherForecast(
      currentTime.toISOString(),
      endTime.toISOString()
    );

    setWeatherData(weatherData);
  }, [currentTime]);

  useEffect(() => {
    fetchGasData();
    fetchWeatherData();
  }, [fetchGasData, fetchWeatherData]);

  const handleDateChange = (dday: -1 | 1) => {
    const newTime = ((dday === -1) ? dayBack : dayForward)(currentTime);
    if (newTime.getTime() <= dayForward(dayForward(startTime)).getTime()) {
      setCurrentTime(newTime);
    }
  };

  const startTimeMs = currentTime.getTime();

  const displayedGasValues = useCallback((data: GasConsumptionData[]) => {
    const startTimeMs = currentTime.getTime();
    const endTimeMs = dayForward(currentTime).getTime();

    const n = Math.floor((endTimeMs - startTimeMs) / (60 * 60 * 1000));

    const a = Array.from<number>({ length: n });
    for (const { time, gas_consumption } of data) {
      const h = Math.floor(
        (new Date(time).getTime() - startTimeMs) / (60 * 60 * 1000)
      );
      if (0 <= h && h < n) {
        a[h] = gas_consumption;
      }
    }
    return a;
  }, [currentTime]);

  const displayedWeatherValues = useCallback((data: WeatherData[]) => {
    const startTimeMs = currentTime.getTime();
    const endTimeMs = dayForward(currentTime).getTime();

    const n = Math.floor((endTimeMs - startTimeMs) / (60 * 60 * 1000));

    const a = Array.from<number>({ length: n });
    for (const { time, air_temperature } of data) {
      const h = Math.floor(
        (new Date(time).getTime() - startTimeMs) / (60 * 60 * 1000)
      );
      if (0 <= h && h < n) {
        a[h] = air_temperature;
      }
    }
    return a;
  }, [currentTime]);

  const hour = (h: number): string =>
    ((h + 11) % 12 + 1) + ((h < 12) ? 'AM' : 'PM');

  const displayedLabels = useCallback(() => {
    const startTimeMs = currentTime.getTime();
    const endTimeMs = dayForward(currentTime).getTime();

    const n = Math.floor((endTimeMs - startTimeMs) / (60 * 60 * 1000));

    const labels = [];
    for (let h = 0; h < n; ++h) {
      const time = new Date(startTimeMs + h * 60 * 60 * 1000);
      const [, , , hours, , ] = toLocalDate(time);
      labels.push(hour(hours));
    }
    return labels;
  }, [currentTime]);

  const weatherGraphData = {
    labels: displayedLabels(),
    datasets: [
      {
        label: 'Temperature (°F)',
        data: displayedWeatherValues(weatherData),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
      },
    ],
  };

  const gasGraphData = {
    labels: displayedLabels(),
    datasets: [
      {
        label: 'Projected gas consumption (MCF)',
        data: displayedGasValues(gasData.projected),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
      },
      {
        label: 'Actual gas consumption (MCF)',
        data: displayedGasValues(gasData.actual),
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
      },
    ],
  };

  const weatherOptions: ChartOptions<'line'> = {
    maintainAspectRatio: false,
    color: 'white',
    scales: {
      x: {
        ticks: {
          color: 'white',
        },
      },
      y: {
        ticks: {
          color: 'white',
        },
      },
    },
  };

  const gasOptions: ChartOptions<'line'> = {
    maintainAspectRatio: false,
    color: 'white',
    scales: {
      x: {
        ticks: {
          color: 'white',
        },
      },
      y: {
        ticks: {
          color: 'white',
        },
      },
    },
  };

  return (
    <div className="flex h-full items-center justify-between p-4 text-white">
      <button
        onClick={() => handleDateChange(-1)}
        disabled={false}
        className="mr-10 h-72 bg-blue-500 p-4 text-4xl text-background"
      >
        {'<'}
      </button>
      <div className="flex h-full w-full flex-col">
        <div className="mb-2 h-[20%] w-full pl-6 pr-4">
          <div className="flex items-end justify-between pb-2">
            HOURLY WEATHER FORECAST
            <span className="rounded bg-white px-4 py-2 text-black">
              {toLocalDateString(currentTime).replace(/,.*/, '')}
            </span>
          </div>
          <div className="flex">
            <div className="flex w-full divide-x divide-gray-600 border-y border-y-gray-600">
              {weatherData.map((weatherItem: WeatherData, index) => (
                <div
                  key={index}
                  className="flex w-[4.16667%] flex-col items-center text-center"
                >
                  <span className="pb-1">{hour(index)}</span>
                  <span className="flex h-6 w-6 items-center justify-center">
                    <img src={weatherItem.icon} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="h-[38%] w-full pb-2">
          <Line data={weatherGraphData} options={weatherOptions} />
        </div>

        <div className="h-[38%] w-full">
          <Line data={gasGraphData} options={gasOptions} />
        </div>
      </div>
      <button
        onClick={() => handleDateChange(1)}
        disabled={false}
        className="ml-10 h-72 bg-blue-500 p-4 text-4xl text-background"
      >
        {'>'}
      </button>
    </div>
  );
};

export default HourlyTrendsPage;
